// ── Local Presence / Geo Calling data model ──

export interface LocalPresenceNumber {
  id: string;
  phoneNumber: string;
  areaCode: string;
  city: string;
  state: string;
  active: boolean;
  providerId: string;
  isDefault: boolean;
}

export interface MarketMapping {
  areaCode: string;
  city: string;
  state: string;
  market: string;
}

export interface CallLogEntry {
  id: string;
  userId: string;
  userName: string;
  targetNumber: string;
  targetName: string;
  selectedOutboundNumber: string;
  selectedOutboundCity: string;
  matchType: "exact" | "market" | "default";
  timestamp: string;
  result: "connected" | "no_answer" | "voicemail" | "busy" | "failed";
}

// ── Florida market mappings ──
export const MARKET_MAPPINGS: MarketMapping[] = [
  { areaCode: "813", city: "Tampa", state: "FL", market: "Tampa Bay" },
  { areaCode: "727", city: "St. Petersburg", state: "FL", market: "Tampa Bay" },
  { areaCode: "941", city: "Sarasota", state: "FL", market: "Suncoast" },
  { areaCode: "239", city: "Fort Myers", state: "FL", market: "Southwest" },
  { areaCode: "863", city: "Lakeland", state: "FL", market: "Central" },
  { areaCode: "352", city: "Ocala", state: "FL", market: "Central" },
  { areaCode: "321", city: "Melbourne", state: "FL", market: "Space Coast" },
  { areaCode: "407", city: "Orlando", state: "FL", market: "Central" },
  { areaCode: "561", city: "West Palm Beach", state: "FL", market: "Southeast" },
  { areaCode: "954", city: "Fort Lauderdale", state: "FL", market: "Southeast" },
  { areaCode: "305", city: "Miami", state: "FL", market: "Southeast" },
  { areaCode: "786", city: "Miami", state: "FL", market: "Southeast" },
  { areaCode: "772", city: "Port St. Lucie", state: "FL", market: "Treasure Coast" },
  { areaCode: "386", city: "Daytona Beach", state: "FL", market: "Northeast" },
  { areaCode: "904", city: "Jacksonville", state: "FL", market: "Northeast" },
  { areaCode: "850", city: "Tallahassee", state: "FL", market: "Panhandle" },
];

// ── Seed number pool ──
export const INITIAL_NUMBER_POOL: LocalPresenceNumber[] = [
  { id: "lp1", phoneNumber: "(813) 555-0100", areaCode: "813", city: "Tampa", state: "FL", active: true, providerId: "twilio-813-001", isDefault: true },
  { id: "lp2", phoneNumber: "(727) 555-0200", areaCode: "727", city: "St. Petersburg", state: "FL", active: true, providerId: "twilio-727-001", isDefault: false },
  { id: "lp3", phoneNumber: "(941) 555-0300", areaCode: "941", city: "Sarasota", state: "FL", active: true, providerId: "twilio-941-001", isDefault: false },
  { id: "lp4", phoneNumber: "(239) 555-0400", areaCode: "239", city: "Fort Myers", state: "FL", active: true, providerId: "twilio-239-001", isDefault: false },
  { id: "lp5", phoneNumber: "(863) 555-0500", areaCode: "863", city: "Lakeland", state: "FL", active: true, providerId: "twilio-863-001", isDefault: false },
  { id: "lp6", phoneNumber: "(352) 555-0600", areaCode: "352", city: "Ocala", state: "FL", active: false, providerId: "twilio-352-001", isDefault: false },
];

// ── Market proximity for fallback ──
const MARKET_PROXIMITY: Record<string, string[]> = {
  "Tampa Bay": ["Suncoast", "Central"],
  "Suncoast": ["Tampa Bay", "Southwest"],
  "Southwest": ["Suncoast", "Tampa Bay"],
  "Central": ["Tampa Bay", "Space Coast"],
  "Space Coast": ["Central", "Treasure Coast"],
  "Southeast": ["Treasure Coast", "Space Coast"],
  "Treasure Coast": ["Southeast", "Space Coast"],
  "Northeast": ["Central", "Panhandle"],
  "Panhandle": ["Northeast"],
};

/**
 * Select the best outbound number for a given target phone number.
 * Priority: 1) exact area code, 2) same market, 3) nearest market, 4) default
 */
export function selectOutboundNumber(
  targetPhone: string,
  numberPool: LocalPresenceNumber[]
): { number: LocalPresenceNumber; matchType: "exact" | "market" | "default" } {
  if (!targetPhone || typeof targetPhone !== "string") {
    const activeNumbers = numberPool.filter((n) => n.active);
    const defaultNum = activeNumbers.find((n) => n.isDefault) || activeNumbers[0];
    return { number: defaultNum || numberPool[0], matchType: "default" };
  }

  const activeNumbers = numberPool.filter((n) => n.active);
  if (activeNumbers.length === 0) {
    return { number: numberPool[0], matchType: "default" };
  }

  const cleaned = targetPhone.replace(/\D/g, "");
  const targetAC = cleaned.length >= 10 ? cleaned.slice(cleaned.length - 10, cleaned.length - 7) : "";

  // 1. Exact area code match
  if (targetAC) {
    const exact = activeNumbers.find((n) => n.areaCode === targetAC);
    if (exact) return { number: exact, matchType: "exact" };
  }

  // 2. Same market match
  if (targetAC) {
    const targetMarket = MARKET_MAPPINGS.find((m) => m.areaCode === targetAC)?.market;
    if (targetMarket) {
      const sameMarket = activeNumbers.find((n) => {
        const nMarket = MARKET_MAPPINGS.find((m) => m.areaCode === n.areaCode)?.market;
        return nMarket === targetMarket;
      });
      if (sameMarket) return { number: sameMarket, matchType: "market" };

      // 3. Nearest market
      const nearMarkets = MARKET_PROXIMITY[targetMarket] || [];
      for (const nm of nearMarkets) {
        const nearMatch = activeNumbers.find((n) => {
          const nMarket = MARKET_MAPPINGS.find((m) => m.areaCode === n.areaCode)?.market;
          return nMarket === nm;
        });
        if (nearMatch) return { number: nearMatch, matchType: "market" };
      }
    }
  }

  // 4. Default
  const defaultNum = activeNumbers.find((n) => n.isDefault) || activeNumbers[0];
  return { number: defaultNum, matchType: "default" };
}

// ── Seed call log ──
export const INITIAL_CALL_LOG: CallLogEntry[] = [
  { id: "cl1", userId: "u8", userName: "Angela Wright", targetNumber: "(813) 555-7890", targetName: "Robert Martinez", selectedOutboundNumber: "(813) 555-0100", selectedOutboundCity: "Tampa", matchType: "exact", timestamp: "2026-03-27 09:15", result: "connected" },
  { id: "cl2", userId: "u8", userName: "Angela Wright", targetNumber: "(941) 555-3456", targetName: "Jennifer Walsh", selectedOutboundNumber: "(941) 555-0300", selectedOutboundCity: "Sarasota", matchType: "exact", timestamp: "2026-03-27 09:45", result: "voicemail" },
  { id: "cl3", userId: "u9", userName: "Chris Delaney", targetNumber: "(407) 555-1234", targetName: "David Chen", selectedOutboundNumber: "(863) 555-0500", selectedOutboundCity: "Lakeland", matchType: "market", timestamp: "2026-03-27 10:20", result: "connected" },
  { id: "cl4", userId: "u8", userName: "Angela Wright", targetNumber: "(239) 555-9876", targetName: "Patricia Howard", selectedOutboundNumber: "(239) 555-0400", selectedOutboundCity: "Fort Myers", matchType: "exact", timestamp: "2026-03-27 11:05", result: "no_answer" },
  { id: "cl5", userId: "u9", userName: "Chris Delaney", targetNumber: "(561) 555-4321", targetName: "Mark Thompson", selectedOutboundNumber: "(813) 555-0100", selectedOutboundCity: "Tampa", matchType: "default", timestamp: "2026-03-26 14:30", result: "connected" },
];
